import { memo, useCallback, useMemo } from 'react'
import { AlertTriangle, ListOrdered, Plus, Scissors, Trash2 } from 'lucide-react'
import {
  LIVE_PART_MAX_BREAK_SEC,
  LIVE_PART_MAX_COUNT,
  LIVE_PART_MAX_TITLE_LENGTH,
  buildLivePartTimeline,
  livePartPlanTotalSec,
  type LivePartDraft,
} from '../../lib/livePartPlan.js'
import { formatDurationShort } from '../../lib/utils.js'
import './LivePartPlanEditor.css'

/**
 * Saisie du déroulé estimé d'un bloc de direct.
 *
 * Un direct occupe un seul élément de grille : ce qu'on saisit ici n'est jamais
 * posé à l'antenne. C'est une estimation, lue par le chrono de régie et publiée
 * à l'EPG en une case par partie. La composition doit donc dire deux choses
 * d'un coup d'œil : *où tombe chaque partie sur l'antenne* et *ce qui reste de
 * bloc* — d'où la barre segmentée et l'horaire calculé sur chaque ligne, tenus
 * à jour pendant que l'opérateur change encore l'heure et la durée du bloc dans
 * le même formulaire.
 *
 * Les durées se saisissent en minutes : une estimation à la seconde serait une
 * fausse précision, et le serveur ne demande pas mieux.
 */

/** Durée proposée pour une coupure ajoutée, en secondes. */
const DEFAULT_BREAK_SEC = 300

/** Durée plancher d'une partie proposée, en secondes. */
const DEFAULT_MIN_PART_SEC = 60

interface LivePartPlanEditorProps {
  /** Début du bloc tel qu'il est en cours d'édition, en ms. */
  blockStartMs: number
  /** Durée du bloc en cours d'édition, en secondes. */
  blockDurationSec: number
  parts: LivePartDraft[]
  onChange: (parts: LivePartDraft[]) => void
  disabled?: boolean
}

function clockAt(ms: number): string {
  return new Date(ms).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
}

/** Minutes affichées dans un champ, arrondies à la minute la plus proche. */
function toMinutes(sec: number): number {
  return Math.max(0, Math.round(sec / 60))
}

export const LivePartPlanEditor = memo(function LivePartPlanEditor({
  blockStartMs, blockDurationSec, parts, onChange, disabled = false,
}: LivePartPlanEditorProps) {
  const blockEndMs = blockStartMs + blockDurationSec * 1000
  const timeline = useMemo(
    () => buildLivePartTimeline(parts, blockStartMs, blockEndMs),
    [parts, blockStartMs, blockEndMs],
  )
  const totalSec = livePartPlanTotalSec(parts)

  const patch = useCallback((index: number, values: Partial<LivePartDraft>) => {
    onChange(parts.map((part, position) => position === index ? { ...part, ...values } : part))
  }, [onChange, parts])

  const remove = useCallback((index: number) => {
    onChange(parts.filter((_, position) => position !== index))
  }, [onChange, parts])

  /**
   * Une partie ajoutée prend la place qui reste, coupure comprise. C'est ce que
   * l'opérateur ferait à la main, et cela évite d'ouvrir la ligne sur un
   * déroulé déjà en dépassement.
   */
  const add = useCallback(() => {
    const freeSec = Math.max(0, blockDurationSec - totalSec - (parts.length ? DEFAULT_BREAK_SEC : 0))
    const next = parts.map((part, position) => position === parts.length - 1
      ? { ...part, breakAfterSec: part.breakAfterSec || DEFAULT_BREAK_SEC }
      : part)
    onChange([...next, {
      title:         null,
      durationSec:   Math.max(DEFAULT_MIN_PART_SEC, Math.round(freeSec / 60) * 60),
      breakAfterSec: 0,
    }])
  }, [blockDurationSec, onChange, parts, totalSec])

  /** Amorce à deux parties : le découpage le plus fréquent, ajustable ensuite. */
  const seed = useCallback(() => {
    const partSec = Math.max(DEFAULT_MIN_PART_SEC, Math.round((blockDurationSec - DEFAULT_BREAK_SEC) / 120) * 60)
    onChange([
      { title: null, durationSec: partSec, breakAfterSec: DEFAULT_BREAK_SEC },
      { title: null, durationSec: partSec, breakAfterSec: 0 },
    ])
  }, [blockDurationSec, onChange])

  const bar = useMemo(() => {
    const spanMs = Math.max(1, blockEndMs - blockStartMs)
    return timeline.segments.flatMap(segment => {
      const cells: Array<{ key: string; kind: 'part' | 'break'; label: string; pct: number }> = [{
        key:   `part-${segment.number}`,
        kind:  'part',
        label: `Partie ${segment.number}`,
        pct:   ((segment.endMs - segment.startMs) / spanMs) * 100,
      }]
      if (segment.breakAfterSec > 0) {
        cells.push({
          key:   `break-${segment.number}`,
          kind:  'break',
          label: `Coupure ${formatDurationShort(segment.breakAfterSec)}`,
          pct:   ((segment.breakAfterSec * 1000) / spanMs) * 100,
        })
      }
      return cells
    })
  }, [blockEndMs, blockStartMs, timeline.segments])

  if (!parts.length) {
    return (
      <section className="live-parts live-parts--empty">
        <header className="live-parts__header">
          <span className="live-parts__title"><ListOrdered size={13} aria-hidden="true" /> Déroulé estimé</span>
        </header>
        <p className="live-parts__help">
          Le direct reste un bloc unique à l’antenne : le déroulé n’y plante rien, il
          annonce seulement les parties à la régie et à l’EPG.
        </p>
        <button type="button" className="tp-button tp-button--outline live-parts__seed" onClick={seed} disabled={disabled}>
          <Scissors size={15} aria-hidden="true" /> Découper en parties estimées
        </button>
      </section>
    )
  }

  return (
    <section className="live-parts">
      <header className="live-parts__header">
        <span className="live-parts__title"><ListOrdered size={13} aria-hidden="true" /> Déroulé estimé</span>
        <span className="live-parts__summary" data-state={timeline.overflowSec > 0 ? 'danger' : 'ok'}>
          {formatDurationShort(totalSec)} annoncées
          {timeline.overflowSec > 0
            ? ` · ${formatDurationShort(timeline.overflowSec)} de trop`
            : timeline.slackSec > 0 ? ` · ${formatDurationShort(timeline.slackSec)} de marge` : ' · bloc plein'}
        </span>
      </header>

      <div className="live-parts__bar" aria-hidden="true">
        {bar.map(cell => (
          <span key={cell.key} className="live-parts__bar-cell" data-kind={cell.kind} style={{ width: `${cell.pct}%` }} title={cell.label} />
        ))}
      </div>

      <ol className="live-parts__list">
        {parts.map((part, index) => {
          const segment = timeline.segments[index]
          const isLast = index === parts.length - 1
          return (
            <li key={index} className="live-parts__row" data-dropped={segment ? undefined : true}>
              <span className="live-parts__number" aria-hidden="true">{index + 1}</span>
              <input
                className="tp-input live-parts__input-title"
                value={part.title ?? ''}
                maxLength={LIVE_PART_MAX_TITLE_LENGTH}
                placeholder={`Partie ${index + 1}`}
                disabled={disabled}
                aria-label={`Intitulé de la partie ${index + 1}`}
                onChange={event => patch(index, { title: event.target.value || null })}
              />
              <label className="live-parts__minutes">
                <input
                  className="tp-input"
                  type="number" min={1} max={Math.floor(blockDurationSec / 60)}
                  value={toMinutes(part.durationSec)}
                  disabled={disabled}
                  aria-label={`Durée estimée de la partie ${index + 1}, en minutes`}
                  onChange={event => patch(index, { durationSec: Math.max(0, Number(event.target.value)) * 60 })}
                />
                <span>min</span>
              </label>
              <label className="live-parts__minutes live-parts__minutes--break" data-hidden={isLast || undefined}>
                <span className="live-parts__break-label">coupure</span>
                <input
                  className="tp-input"
                  type="number" min={0} max={LIVE_PART_MAX_BREAK_SEC / 60}
                  value={toMinutes(part.breakAfterSec)}
                  disabled={disabled || isLast}
                  aria-label={`Coupure estimée après la partie ${index + 1}, en minutes`}
                  onChange={event => patch(index, { breakAfterSec: Math.max(0, Number(event.target.value)) * 60 })}
                />
                <span>min</span>
              </label>
              <span className="live-parts__window">
                {segment
                  ? `${clockAt(segment.startMs)} → ${clockAt(segment.endMs)}${segment.truncated ? ' ·  tronquée' : ''}`
                  : 'hors du bloc'}
              </span>
              <button
                type="button"
                className="tp-icon-button live-parts__remove"
                onClick={() => remove(index)}
                disabled={disabled}
                aria-label={`Retirer la partie ${index + 1}`}
              >
                <Trash2 size={14} aria-hidden="true" />
              </button>
            </li>
          )
        })}
      </ol>

      {timeline.overflowSec > 0 && (
        <p className="live-parts__warning" role="alert">
          <AlertTriangle size={14} aria-hidden="true" />
          Le déroulé dépasse le bloc de {formatDurationShort(timeline.overflowSec)} : raccourcissez une
          partie ou une coupure, sinon l’enregistrement sera refusé.
        </p>
      )}

      <div className="live-parts__actions">
        <button
          type="button" className="tp-button tp-button--ghost"
          onClick={add} disabled={disabled || parts.length >= LIVE_PART_MAX_COUNT}
        >
          <Plus size={14} aria-hidden="true" /> Ajouter une partie
        </button>
        <button type="button" className="tp-button tp-button--ghost live-parts__clear" onClick={() => onChange([])} disabled={disabled}>
          Effacer le déroulé
        </button>
      </div>
    </section>
  )
})
